import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { Printer, Award, ArrowLeft } from "lucide-react";

export default function Certificate() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get(`/courses/${id}/certificate`)
      .then((r) => setData(r.data))
      .catch(() => setErr(true));
  }, [id]);

  if (err) return <Navigate to="/library" replace />;
  if (!data) return <Layout><div className="container-narrow py-32 overline text-brand-inkMuted">Loading…</div></Layout>;

  if (!data.eligible) {
    return (
      <Layout>
        <section className="container-narrow py-24 max-w-xl text-center" data-testid="certificate-locked">
          <Award className="w-10 h-10 mx-auto text-brand-inkMuted" />
          <h1 className="font-display text-4xl mt-6 text-brand-ink">Certificate locked</h1>
          <p className="mt-3 text-brand-inkMuted">Pass the final exam for <em>{data.course_title}</em> with at least {data.needed}% to unlock your Certificate of Completion.</p>
          <Link to={`/learn/${id}`} className="inline-block mt-8 bg-brand-ink text-white hover:bg-black px-7 py-3 text-sm font-medium tracking-wide transition-colors">Take the final exam</Link>
        </section>
      </Layout>
    );
  }

  const passedDate = new Date(data.passed_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

  return (
    <Layout hideFooter>
      {/* Toolbar — hidden in print */}
      <div className="container-narrow py-6 flex items-center justify-between print:hidden">
        <Link to={`/learn/${id}`} className="overline text-brand-inkMuted hover:text-brand-ink inline-flex items-center gap-2" data-testid="back-to-course"><ArrowLeft className="w-4 h-4" /> Back to course</Link>
        <button
          data-testid="print-certificate"
          onClick={() => window.print()}
          className="inline-flex items-center gap-2 bg-brand-ink text-white hover:bg-black px-5 py-2.5 text-sm font-medium tracking-wide transition-colors"
        >
          <Printer className="w-4 h-4" /> Print / Save as PDF
        </button>
      </div>

      {/* The certificate itself */}
      <section className="container-narrow pb-20">
        <div data-testid="certificate-card" className="bg-white border-[12px] border-brand-ink p-12 md:p-20 relative grain print:border-0 print:p-12">
          <div className="absolute inset-4 border border-brand-line pointer-events-none print:hidden" />
          <div className="relative text-center">
            <div className="flex items-center justify-center gap-2">
              <span className="inline-block w-2 h-2 bg-brand-rust" />
              <span className="font-serif text-xl tracking-tight">South on Leadership</span>
            </div>
            <p className="overline text-brand-rust mt-12">Certificate of Completion</p>
            <h1 className="font-display text-5xl md:text-7xl mt-6 text-brand-ink leading-[1.05]">
              <em className="italic">This certifies that</em>
            </h1>
            <p className="font-display text-4xl md:text-5xl mt-8 text-brand-ink" data-testid="cert-name">{data.user_name}</p>

            <div className="mt-12 max-w-2xl mx-auto">
              <p className="text-lg text-brand-inkMuted leading-relaxed">
                has successfully completed
              </p>
              <p className="font-display text-3xl md:text-4xl mt-4 text-brand-ink" data-testid="cert-course">{data.course_title}</p>
              <p className="text-brand-inkMuted mt-3">with {data.instructor}</p>
            </div>

            <div className="mt-16 grid grid-cols-3 gap-8 max-w-2xl mx-auto">
              <div>
                <p className="overline text-brand-inkMuted">Date</p>
                <p className="font-serif text-lg mt-2 text-brand-ink">{passedDate}</p>
              </div>
              <div>
                <p className="overline text-brand-inkMuted">Score</p>
                <p className="font-serif text-lg mt-2 text-brand-ink" data-testid="cert-score">{data.score}%</p>
              </div>
              <div>
                <p className="overline text-brand-inkMuted">Certificate ID</p>
                <p className="font-mono text-xs mt-2 text-brand-inkMuted break-all">{data.certificate_id}</p>
              </div>
            </div>

            <div className="mt-16 flex items-center justify-center gap-3">
              <span className="inline-block w-2 h-2 bg-brand-rust" />
              <span className="overline text-brand-inkMuted">South on Leadership · Books, courses, and field notes for leaders</span>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
